<template>
    <div class="container overview-grid" :class="{revoked: isRevoked}">
        <div v-if="!isRevoked" class="top mobile-only">
            <a role="button" class="top__expand ml-1" href="#" @click.prevent="expandDetails" @touch.prevent="expandDetails">
                {{ $t('orderCheckout.mobile-expand-details') }} <i class="mdi mdi-chevron-right" :class="{active: detailsExpanded}"></i>
            </a>
            <span class="top__price">
                {{ formatter.format(order.unpaid_amount) }}
            </span>
        </div>

        <div v-if="!isRevoked" class="left">
            <h1 class="heading desktop-only" v-html="$t('orderCheckout.overview-title', {id: this.order.id})"></h1>

            <div class="wrapper mx-md-5">
                <Stepper :steps="stepperSteps"/>
            </div>

            <template v-if="isWaitingForSubscriptionConfirmation && !waitingForSubscriptionConfirmationFailed">
                <fieldset class="mx-2">
                    <legend align="center" v-html="$t('orderCheckout.select-payment-method')"></legend>

                    <div class="bg-light rounded-bottom p-3 px-4 py-3">
                        <div class="text-center" v-html="$t('orderCheckout.processing-payment')"></div>
                    </div>
                </fieldset>
            </template>
            <PaymentMethods
                v-else
                :countries="countries"
                :disable-payment-method="disablePaymentMethod"
                :order="order"
                :payment-methods="paymentMethods"
                :price="order.price"
                :csrf-token="csrfToken"
                :stripe-payment-intent-api-url="stripePaymentIntentApiUrl"
                @terminate-subscription="$refs.terminateSubscriptionModal.onTerminatePaymentSubscription()"
            />
        </div>

        <h4
            v-if="isRevoked"
            class="text-primary text-center"
            v-html="$t('orderRevocation.revokedModalTitle', { id: this.order.id })"
        ></h4>

        <component
            :is="detailsComponent"
            v-if="detailsExpanded || isRevoked"
            :order="order"
            :combined-suborders="combinedSuborders"
            :advantages="advantages"
            :formatter="formatter"
            :gift-voucher-payments="giftVoucherPayments"
            :is-revoked="isRevoked"
        />

        <TerminateSubscriptionModal
            ref="terminateSubscriptionModal"
            :terminate-subscription-url="terminateSubscriptionUrl"
            @done="window.location.reload()" />

        <loading-animation
            :full-page-loader="true"
            :visible="loading"
        ></loading-animation>
    </div>
</template>

<script>
import Stepper from '../../Stepper.vue';
import PaymentMethods from './PaymentMethods.vue';
import Order from './Details/Order.vue';
import VoucherOrder from './Details/VoucherOrder.vue';
import TerminateSubscriptionModal from '../Modal/TerminateSubscriptionModal.vue';
import { echo } from '../../../echo.js';

export default {
    name: 'OrderCheckout',
    components: {TerminateSubscriptionModal, Stepper, PaymentMethods},
    props: [
        'countries',
        'initialOrder',
        'paymentMethods',
        'csrfToken',
        'initialDelayedOrderNoticeStatus',
        'combinedSuborders',
        'advantages',
        'disablePaymentMethod',
        'giftVoucherPayments',
        'terminateSubscriptionUrl',
        'stripePaymentIntentApiUrl',
        'waitingForSubscriptionConfirmation',
        'waitingForSubscriptionConfirmationTimeout',
        'settings',
    ],
    provide() {
        return {
            order: this.order,
            settings: this.settings,
        };
    },
    data() {
        return {
            formatter: new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            }),
            loading: false,
            order: this.initialOrder,
            detailsExpanded: true,
            mediaQuery: null,
            stepperSteps: [
                {name: this.$t('orderCheckout.step-1'), completed: true},
                {name: this.$t('orderCheckout.step-2'), completed: true},
                {name: this.$t('orderCheckout.step-3'), active: true},
                {name: this.$t('orderCheckout.step-4')},
            ],
            waitingForSubscriptionConfirmationFailed: false,
        };
    },
    methods: {
        expandDetails() {
            this.detailsExpanded = !this.detailsExpanded;
        },
        reloadPage() {
            // If we do not have a timeout, then reload the page to make sure that the customer will see
            // the current status of his order. But avoid the submitting of any form, which could be placed
            // on the current page.
            window.location.href = location.href;
        },
    },
    computed: {
        detailsComponent() {
            switch (this.initialOrder.type) {
                case 'voucher_order':
                    return VoucherOrder;
                default:
                    return Order;
            }
        },
        isRevoked() {
            return !!this.order.revoked;
        },
        isWaitingForSubscriptionConfirmation() {
            return !!this.waitingForSubscriptionConfirmation;
        },
    },
    mounted() {
        this.mediaQuery = window.matchMedia('(min-width: 992px)');
        this.mediaQuery.addEventListener('change', () => {
            this.detailsExpanded = this.mediaQuery.matches;
        });
        this.detailsExpanded = this.mediaQuery.matches;

        // If the customer selects a payment method, were we have to wait for the confirmation of the subscription,
        // we have to load the echo script to register a listener for the event handler. Because we want to avoid
        // a request spamming to the host, to poll the status of the payment. Otherwise, if the configured timeout
        // is reached, we have to display the payment methods again.
        if (this.isWaitingForSubscriptionConfirmation) {
            setTimeout(() => {
                this.reloadPage();
            }, this.waitingForSubscriptionConfirmationTimeout * 60 * 1000);
            echo
                .private('orders.' + this.order.hash.replace(/-/g, '_'))
                .listen('DelayedPaymentConfirmation', (event) => {
                    const isTimeout = typeof event.timeout === 'boolean' && event.timeout;
                    if (isTimeout) {
                        this.waitingForSubscriptionConfirmationFailed = true;
                        return;
                    }

                    this.reloadPage();
                });
        }
    },
    beforeUnmount() {
        this.mediaQuery.removeEventListener('change');
    },
};
</script>

<style lang="scss" scoped>
.overview-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: max-content;

    &.revoked {
        grid-template-columns: 50%;
        justify-content: center;
    }

    padding: 140px 0 0;

    text-align: left;

    @media screen and (max-width: 992px) {
        grid-template-columns: 100% !important;
        gap: 1rem;
        padding-bottom: 2rem;
    }

    &::v-deep .heading {
        font-size: xx-large;

        @media screen and (max-width: 992px) {
            font-size: x-large;
        }

        &.tight {
            line-height: 1;
        }
    }

    & > .top {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 1rem;

        .top__expand {
            display: flex;
            align-items: center;
            gap: 0.2rem;
        }
    }

    & > .left {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        @media screen and (max-width: 992px) {
            order: 2;
        }
    }

    fieldset {
        border-width: 2pt;
        border-style: solid;
        border-color: #dee2e1;
        border-image: initial;
        border-radius: 12px;

        transition: all 0.6s cubic-bezier(1, 0.01, 1, 0.01);

        legend {
            all: revert;
            text-align: center;
            font-size: xx-large;
            font-weight: 600;
            color: var(--main-color);
        }
    }
}

.mdi-chevron-right {
    transition: transform 0.3s ease;

    &.active {
        transform: rotate(90deg);
    }
}
</style>
